import React, { useCallback, useRef } from "react";
import DataGrid, { Column, Editing, Paging } from "devextreme-react/data-grid";

import { useDispatch, useSelector } from "react-redux";
import {
  postResource,
  updateResource,
  deleteResource,
  getResourceAPI,
} from "../../redux/slices/resourceSlice.js";
import { handleNotificationMessage } from "../../utils/notifications.js";
import { DateBox } from "devextreme-react";
import { fromCustomDate, toCustomDate } from "../../utils/date.js";
import { getUserId } from "../../redux/slices/userSlice.js";
import CustomSelection from "../generic/CustomSelection.jsx";
import DateDisplay from "../generic/DateDisplay.jsx";
import { getUIProperty } from "../../redux/slices/uiSlice.js";

const columns = [
  { dataField: "id", allowEditing: false, caption: "Id" },

  {
    dataField: "type",
    caption: "Type",
  },
  { dataField: "description", caption: "Description" },
  { dataField: "serial", caption: "serial" },
  { dataField: "vessel_id", caption: "Vessel Id" },
];

const resourceName = "vessel-system";
const VesselSystemGrid = ({ data }) => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const userId = useSelector((state) => getUserId(state));
  const clientId = useSelector((state) => getUIProperty(state, "clientId"));

  const dataSource = data.data.vessel_systems;

  const onValueChanged = useCallback((rowIndex, value, dataField) => {
    gridRef.current.instance.cellValue(rowIndex, dataField, value);
  }, []);

  const onInitNewRow = useCallback(
    ({ data }) => {
      data.user_id = userId;
    },
    [userId]
  );

  const recallData = useCallback(() => {
    dispatch(
      getResourceAPI({
        path: "client-search-details",
        query: { client_id: clientId },
      })
    );
  }, [clientId, dispatch]);

  const onSaving = useCallback(
    (e) => {
      e.changes.forEach((change) => {
        switch (change.type) {
          case "insert":
            dispatch(
              postResource({
                path: `${resourceName}`,
                data: change.data,
              })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();

              recallData();

              const { path, error } = payload;
              const data = payload.data;
              if (!data) return;
              const { id } = data;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          case "update":
            dispatch(
              updateResource({
                path: resourceName,
                id: change.key.id,
                data: change.data,
              })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();

              recallData();

              const { path, id, error } = payload;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          case "remove":
            dispatch(
              deleteResource({ path: resourceName, id: change.key.id })
            ).then(({ payload }) => {
              e.cancel = true;
              e.component.cancelEditData();
              recallData();

              const { path, id, error } = payload;

              handleNotificationMessage(path, id, error, change.type);
            });
            return;
          default:
            return;
        }
      });

      e.cancel = true;
    },
    [dispatch, recallData]
  );

  return (
    <React.Fragment>
      <h2 style={{ textAlign: "center" }}>Vessel Systems</h2>
      <DataGrid
        id="gridContainer"
        rowAlternationEnabled={true}
        dataSource={dataSource}
        allowColumnReordering={true}
        showBorders={true}
        onSaving={onSaving}
        onInitNewRow={onInitNewRow}
        ref={gridRef}
      >
        <Paging enabled={true} />
        <Editing
          mode="batch"
          allowUpdating={true}
          allowAdding={true}
          allowDeleting={true}
          allowSorting={false}
        />
        {columns.map((column, idx) => {
          if (column.type === "customSelection")
            return (
              <Column
                key={column.dataField}
                dataField={column.dataField}
                caption={column.caption}
                width={150}
                editCellRender={(cell) => {
                  return (
                    <CustomSelection
                      value={cell.data[column.dataField]}
                      dataSource={column.dataSource}
                      handleChange={(e) =>
                        onValueChanged(cell.rowIndex, e.value, column.dataField)
                      }
                    />
                  );
                }}
              />
            );
          else if (column.type === "date")
            return (
              <Column
                key={column.dataField}
                dataField={column.dataField}
                caption={column.caption}
                width={150}
                cellRender={DateDisplay}
                editCellRender={(cell) => (
                  <DateBox
                    displayFormat="dd/MM/yyyy"
                    onValueChanged={(e) =>
                      onValueChanged(
                        cell.rowIndex,
                        toCustomDate(e.value),
                        column.dataField
                      )
                    }
                    value={fromCustomDate(cell.data[column.dataField])}
                  />
                )}
              />
            );
          else
            return (
              <Column
                key={column.dataField}
                cellRender={column.cellRender}
                dataField={column.dataField}
                allowEditing={column.allowEditing}
                caption={column.caption}
                width={column.width}
              />
            );
        })}
      </DataGrid>
    </React.Fragment>
  );
};
export default VesselSystemGrid;
